import styled from "styled-components";
import { COLORS } from "../../styles/colors";
import { breakpoint } from "../../styles/constants";

export interface WxRadioProps {
  label: string;
  radioName: string;
  value: string;
  noLabel?: boolean;
  disabled?: boolean;
  selected?: boolean;
  onChange: () => void;
  allowUnselect?: boolean;
  darkMode?: boolean;
}

const WxRadio = ({
  label,
  radioName,
  noLabel = false,
  selected = false,
  disabled = false,
  allowUnselect = false,
  value,
  onChange,
  darkMode = false,
}: WxRadioProps) => {
  return (
    <LabelContainer
      data-nolabel={noLabel}
      data-disabled={disabled}
      onClick={() => allowUnselect && onChange()}
    >
      <AlignDiv>
        {allowUnselect ? (
          <FakeRadio selected={selected}>
            <div />
          </FakeRadio>
        ) : (
          <InputRadio
            type="radio"
            name={radioName}
            onChange={onChange}
            disabled={disabled}
            value={value}
            checked={selected}
          />
        )}
      </AlignDiv>
      {!noLabel && <P darkMode={darkMode}>{label}</P>}
    </LabelContainer>
  );
};

const LabelContainer = styled.label`
  --border-color: ${COLORS.black};
  --radio-color: ${COLORS.red};

  &[data-nolabel="true"] {
    --radio-color: ${COLORS.red};
  }

  &[data-disabled="true"] {
    --border-color: ${COLORS.red};
    --radio-color: ${COLORS.red};
  }

  --radio-size: 1rem;

  cursor: pointer;
  font-weight: 300;
  line-height: 0.875rem;

  p {
    color: ${COLORS.black};
  }

  display: grid;
  grid-template-columns: 1em auto;
  gap: 0.625rem;
`;

const InputRadio = styled.input`
  &[type="radio"] {
    font: inherit;
    margin: 0;
    color: var(--border-color);
    width: var(--radio-size);
    height: var(--radio-size);
    border: 1px solid var(--border-color);
    border-radius: 50%;
    transform: translateY(-0.5px);
    display: grid;
    place-content: center;
  }

  &[type="radio"]::before {
    content: "";
    font: inherit;
    width: calc(var(--radio-size) / 1.6);
    height: calc(var(--radio-size) / 1.6);
    border-radius: 50%;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em var(--radio-color);
  }

  &[type="radio"]:checked::before {
    transform: scale(1);
  }
`;

const FakeRadio = styled.div<{ selected: boolean }>`
  font: inherit;
  margin: 0;
  color: var(--border-color);
  width: var(--radio-size);
  height: var(--radio-size);
  border: 1px solid var(--border-color);
  border-radius: 50%;
  transform: translateY(-0.5px);
  display: grid;
  place-content: center;

  > div {
    font: inherit;
    width: calc(var(--radio-size) / 1.6);
    height: calc(var(--radio-size) / 1.6);
    border-radius: 50%;
    background-color: ${COLORS.red};
    transform: scale(${({ selected }) => (selected ? 1 : 0)});
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em var(--radio-color);
  }
`;

const P = styled.p<{ darkMode: boolean }>`
  color: ${({ darkMode }) => (darkMode ? COLORS.white : COLORS.black)};
  font-size: 0.75rem;
  @media (min-width: ${breakpoint}) {
    font-size: 0.875rem;
  }
`;

export const AlignDiv = styled.div`
  width: 20px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

export default WxRadio;
